// Resume48.js - Complete with all backend data fetching and pagination (FIXED)
import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import {
  IconButton,
  Typography,
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Paper,
} from '@mui/material';
import {
  ChevronLeft,
  ChevronRight,
  Reorder,
  DragIndicator,
} from '@mui/icons-material';

// Default section order
const defaultLeftSectionOrder = ['skills', 'education', 'certifications', 'languages', 'softwareSkills', 'interests'];
const defaultRightSectionOrder = ['summary', 'experience', 'accomplishments', 'volunteering', 'websites', 'additionalInfo', 'references'];

export const resumeMeta = {
  hasPhoto: true,
  columns: 2,
};

const pageSizeMap = {
  A4: { width: "210mm", height: "297mm" },
  Letter: { width: "216mm", height: "279mm" },
  Legal: { width: "216mm", height: "356mm" },
  A3: { width: "297mm", height: "420mm" },
  Executive: { width: "184mm", height: "267mm" },
};

const pageSizePxMap = {
  A4:        { width: 794,  height: 1123 },
  Letter:    { width: 816,  height: 1056 },
  Legal:     { width: 816,  height: 1344 },
  A3:        { width: 1123, height: 1587 },
  Executive: { width: 696,  height: 1010 },
};

const Resume48 = ({
  color = "#0f2b5b",
  font = "Arial, Helvetica, sans-serif",
  fontSize = "14px",
  headingSize = "24px",
  fontStyle = "normal",
  sectionSpacing = 50,
  paragraphSpacing = 30,
  lineSpacing = 20,
  topBottomMargin = 40,
  sideMargins = 40,
  paragraphIndent = 0,
  lineWeight = 1,
  pageSize = "A4",
  photo: photoProp = "",
  formData: formDataProp = {},
  workExperiences: workExperiencesProp = [],
  exporting = false,
  enableDragDrop = true,
}) => {
  const page = pageSizeMap[pageSize] || pageSizeMap.A4;
  const pagePx = pageSizePxMap[pageSize] || pageSizePxMap.A4;

  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  
  // Store page content per page
  const [pageContents, setPageContents] = useState({});

  // Section ordering states
  const [leftSectionOrder, setLeftSectionOrder]   = useState(defaultLeftSectionOrder);
  const [rightSectionOrder, setRightSectionOrder] = useState(defaultRightSectionOrder);
  const [dragOverIndex, setDragOverIndex]         = useState(null);
  const [dragOverSide, setDragOverSide]           = useState(null);
  const [arrangeDialogOpen, setArrangeDialogOpen] = useState(false);

  const [formData, setFormData] = useState({
    firstName: "", lastName: "", city: "", country: "", pinCode: "",
    phone: "", email: "", currentPosition: "", linkedIn: "", website: "", drivingLicence: "",
  });
  const [photo, setPhoto]                           = useState("");
  const [workHistory, setWorkHistory]               = useState([]);
  const [education, setEducation]                   = useState([]);
  const [educationEntries, setEducationEntries]     = useState([]);
  const [savedSkills, setSavedSkills]               = useState([]);
  const [references, setReferences]                 = useState([]);
  const [savedReferences, setSavedReferences]       = useState([]);
  const [profileSummary, setProfileSummary]         = useState("");
  const [savedSummary, setSavedSummary]             = useState("");
  const [savedAdditionalInfo, setSavedAdditionalInfo] = useState([]);
  const [savedLanguages, setSavedLanguages]         = useState([]);
  const [savedAccomplishments, setSavedAccomplishments] = useState([]);
  const [savedCertifications, setSavedCertifications] = useState([]);
  const [softwareSkills, setSoftwareSkills]         = useState([]);
  const [savedVolunteering, setSavedVolunteering]   = useState([]);
  const [savedInterests, setSavedInterests]         = useState([]);
  const [savedWebsites, setSavedWebsites]           = useState([]);
  const [customSections, setCustomSections]         = useState({});
  const [loading, setLoading]                       = useState(true);

  const rightContentRef = useRef(null);
  const sidebarContentRef = useRef(null);

  const formatDate = (month, year) => {
    if (!month || !year) return "";
    return `${month} ${year}`;
  };

  // ─── overflow-safe inline style helpers ───────────────────────────────────
  const safeText = {
    wordWrap: "break-word",
    overflowWrap: "break-word",
    wordBreak: "break-word",
    maxWidth: "100%",
    boxSizing: "border-box",
  };

  const baseText = (extra = {}) => ({
    fontFamily: font,
    fontSize: fontSize,
    fontStyle: fontStyle,
    lineHeight: `${lineSpacing / 20}`,
    ...safeText,
    ...extra,
  });

  const headingStyle = (extra = {}) => ({
    fontFamily: font,
    fontSize: headingSize,
    fontStyle: fontStyle,
    fontWeight: "bold",
    marginBottom: `${paragraphSpacing}px`,
    color: "#ffffff",
    textTransform: "uppercase",
    letterSpacing: "1.6px",
    ...safeText,
    ...extra,
  });

  const rightHeadingStyle = (extra = {}) => ({
    fontFamily: font,
    fontSize: headingSize,
    fontStyle: fontStyle,
    fontWeight: "bold",
    marginBottom: `${paragraphSpacing / 2}px`,
    color: color,
    textTransform: "uppercase",
    letterSpacing: "1.2px",
    ...safeText,
    ...extra,
  });

  const scopedStyleTag = `
    <style>
      .r48-html * {
        font-family: ${font} !important;
        font-size: ${fontSize} !important;
        font-style: ${fontStyle} !important;
        line-height: ${lineSpacing / 20} !important;
        word-wrap: break-word !important;
        overflow-wrap: break-word !important;
        word-break: break-word !important;
        max-width: 100% !important;
        box-sizing: border-box !important;
      }
      .r48-html p   { margin-bottom: ${paragraphSpacing / 10}px !important; }
      .r48-html li  { margin-bottom: ${paragraphSpacing / 20}px !important; }
      .r48-html ul, .r48-html ol { padding-left: 20px !important; }
    </style>
  `;

  const wrapHtml = (html) =>
    `${scopedStyleTag}<div class="r48-html" style="width:100%;max-width:100%;overflow:hidden;word-break:break-word;box-sizing:border-box;">${html}</div>`;

  useEffect(() => {
    const fetchAllData = async () => {
      try {
        const token = localStorage.getItem("token");

        const storedEntries = localStorage.getItem("educationEntries");
        if (storedEntries) setEducationEntries(JSON.parse(storedEntries));

        const storedSkills = localStorage.getItem("skills");
        if (storedSkills) setSavedSkills(JSON.parse(storedSkills));

        const storedSummaries = JSON.parse(localStorage.getItem("summaries")) || [];
        if (storedSummaries.length > 0) {
          const s = storedSummaries[storedSummaries.length - 1];
          setSavedSummary(s);
          setProfileSummary(s);
        }

        setSavedAdditionalInfo(JSON.parse(localStorage.getItem("additionalInfo")) || []);
        setSavedLanguages(JSON.parse(localStorage.getItem("languagesList")) || []);
        setSavedAccomplishments(JSON.parse(localStorage.getItem("accomplishmentsList")) || []);
        setSavedCertifications(JSON.parse(localStorage.getItem("certificationsList")) || []);
        setSavedReferences(JSON.parse(localStorage.getItem("referencesList")) || []);
        setSoftwareSkills(JSON.parse(localStorage.getItem("softwareSkills")) || []);
        setSavedVolunteering(JSON.parse(localStorage.getItem("volunteering")) || []);
        setSavedInterests(JSON.parse(localStorage.getItem("interests")) || []);
        setSavedWebsites(JSON.parse(localStorage.getItem("websites")) || []);

        const userId = localStorage.getItem("userId");
        let sectionsData = {};
        if (userId) {
          const currentCustomSections = JSON.parse(localStorage.getItem(`current_custom_sections_${userId}`)) || [];
          currentCustomSections.forEach(sectionName => {
            try {
              const raw = localStorage.getItem(`custom_${userId}_${sectionName}`);
              if (raw) {
                const parsed = JSON.parse(raw);
                const validItems = (parsed.items || []).filter(item => item && item.trim() !== '');
                if (validItems.length > 0) sectionsData[sectionName] = validItems;
              }
            } catch (e) {
              console.error(`Error loading custom section ${sectionName}:`, e);
            }
          });
        }
        setCustomSections(sectionsData);

        const savedLeftOrder = localStorage.getItem("resume48LeftSectionOrder");
        const savedRightOrder = localStorage.getItem("resume48RightSectionOrder");
        const currentCustomIds = Object.keys(sectionsData).map(n => `custom_${n}`);
        
        if (savedLeftOrder) {
          try {
            const parsedLeft = JSON.parse(savedLeftOrder).filter(s => !s.startsWith('custom_'));
            setLeftSectionOrder(parsedLeft);
          } catch {}
        }
        
        if (savedRightOrder) {
          try {
            const parsedRight = JSON.parse(savedRightOrder);
            const filteredRight = parsedRight.filter(s => !s.startsWith('custom_'));
            setRightSectionOrder([...filteredRight, ...currentCustomIds]);
          } catch {
            setRightSectionOrder([...defaultRightSectionOrder, ...currentCustomIds]);
          }
        } else {
          setRightSectionOrder([...defaultRightSectionOrder, ...currentCustomIds]);
        }

        if (photoProp) setPhoto(photoProp);
        if (formDataProp && Object.keys(formDataProp).length > 0) setFormData(formDataProp);
        if (workExperiencesProp && workExperiencesProp.length > 0) setWorkHistory(workExperiencesProp);

        if (!token) { setLoading(false); return; }

        try {
          const contactRes = await axios.get("https://resume-maker-lc.onrender.com/api/contact/me", {
            headers: { Authorization: `Bearer ${token}` },
          });
          if (contactRes.data) {
            setFormData(prev => ({
              firstName:       contactRes.data.firstName       || prev.firstName       || "",
              lastName:        contactRes.data.lastName        || prev.lastName        || "",
              city:            contactRes.data.city            || prev.city            || "",
              country:         contactRes.data.country         || prev.country         || "",
              pinCode:         contactRes.data.pinCode         || prev.pinCode         || "",
              phone:           contactRes.data.phone           || prev.phone           || "",
              email:           contactRes.data.email           || prev.email           || "",
              currentPosition: contactRes.data.currentPosition || prev.currentPosition || "",
              linkedIn:        contactRes.data.linkedIn        || prev.linkedIn        || "",
              website:         contactRes.data.website         || prev.website         || "",
              drivingLicence:  contactRes.data.drivingLicence  || prev.drivingLicence  || "",
            }));
            if (contactRes.data.photo && !photoProp) {
              setPhoto(contactRes.data.photo.startsWith("http")
                ? contactRes.data.photo
                : `https://resume-maker-lc.onrender.com${contactRes.data.photo}`);
            }
          }
        } catch (e) { console.log("No contact data found"); }

        if (workExperiencesProp.length === 0) {
          try {
            const workRes = await axios.get("https://resume-maker-lc.onrender.com/api/work/me", {
              headers: { Authorization: `Bearer ${token}` },
            });
            if (workRes.data && Array.isArray(workRes.data)) setWorkHistory(workRes.data);
          } catch (e) { console.log("No work history found"); }
        }

        try {
          const eduRes = await axios.get("https://resume-maker-lc.onrender.com/api/education/me", {
            headers: { Authorization: `Bearer ${token}` },
          });
          if (eduRes.data && Array.isArray(eduRes.data)) setEducation(eduRes.data);
        } catch (e) { console.log("No education found"); }

        try {
          const refRes = await axios.get("https://resume-maker-lc.onrender.com/api/references/me", {
            headers: { Authorization: `Bearer ${token}` },
          });
          if (refRes.data && Array.isArray(refRes.data)) setReferences(refRes.data);
        } catch (e) { console.log("No references found"); }

        setLoading(false);
      } catch (err) {
        console.error("Error fetching data:", err);
        setLoading(false);
      }
    };
    fetchAllData();
  }, []);

  useEffect(() => {
    if (photoProp) setPhoto(photoProp);
  }, [photoProp]);

  useEffect(() => {
    if (formDataProp && Object.keys(formDataProp).length > 0) {
      setFormData(prev => ({
        ...prev,
        firstName:       formDataProp.firstName       !== undefined ? formDataProp.firstName       : prev.firstName,
        lastName:        formDataProp.lastName        !== undefined ? formDataProp.lastName        : prev.lastName,
        city:            formDataProp.city            !== undefined ? formDataProp.city            : prev.city,
        country:         formDataProp.country         !== undefined ? formDataProp.country         : prev.country,
        pinCode:         formDataProp.pinCode         !== undefined ? formDataProp.pinCode         : prev.pinCode,
        phone:           formDataProp.phone           !== undefined ? formDataProp.phone           : prev.phone,
        email:           formDataProp.email           !== undefined ? formDataProp.email           : prev.email,
        currentPosition: formDataProp.currentPosition !== undefined ? formDataProp.currentPosition : prev.currentPosition,
        linkedIn:        formDataProp.linkedIn        !== undefined ? formDataProp.linkedIn        : prev.linkedIn,
        website:         formDataProp.website         !== undefined ? formDataProp.website         : prev.website,
        drivingLicence:  formDataProp.drivingLicence  !== undefined ? formDataProp.drivingLicence  : prev.drivingLicence,
      }));
    }
  }, [formDataProp]);

  useEffect(() => {
    if (workExperiencesProp && workExperiencesProp.length > 0) {
      setWorkHistory(workExperiencesProp);
    }
  }, [workExperiencesProp]);

  useEffect(() => {
    if (leftSectionOrder.length > 0) {
      localStorage.setItem("resume48LeftSectionOrder", JSON.stringify(leftSectionOrder));
    }
    if (rightSectionOrder.length > 0) {
      localStorage.setItem("resume48RightSectionOrder", JSON.stringify(rightSectionOrder));
    }
  }, [leftSectionOrder, rightSectionOrder]);

  // Drag handlers
  const mkDragStart = (side, order) => (e, idx) => {
    e.dataTransfer.setData("text/plain", JSON.stringify({ idx, side, id: order[idx] }));
    e.currentTarget.style.opacity = "0.5";
  };
  const handleDragOver  = (e, idx, side) => { e.preventDefault(); setDragOverIndex(idx); setDragOverSide(side); };
  const handleDragLeave = () => { setDragOverIndex(null); setDragOverSide(null); };
  const handleDragEnd   = e  => { e.currentTarget.style.opacity = "1"; setDragOverIndex(null); setDragOverSide(null); };

  const handleDrop = (e, targetIdx, targetSide) => {
    e.preventDefault();
    const { idx: srcIdx, side: srcSide, id } = JSON.parse(e.dataTransfer.getData("text/plain"));
    let lo = [...leftSectionOrder], ro = [...rightSectionOrder];
    if (srcSide === targetSide) {
      const arr = srcSide === "left" ? lo : ro;
      const [m] = arr.splice(srcIdx, 1);
      arr.splice(targetIdx, 0, m);
    } else {
      (srcSide === "left" ? lo : ro).splice(srcIdx, 1);
      (targetSide === "left" ? lo : ro).splice(targetIdx, 0, id);
    }
    setLeftSectionOrder(lo);  localStorage.setItem("resume48LeftSectionOrder",  JSON.stringify(lo));
    setRightSectionOrder(ro); localStorage.setItem("resume48RightSectionOrder", JSON.stringify(ro));
    setDragOverIndex(null); setDragOverSide(null);
    e.currentTarget.style.opacity = "1";
  };

  const resetAll = () => {
    setLeftSectionOrder(defaultLeftSectionOrder);
    setRightSectionOrder(defaultRightSectionOrder);
    localStorage.removeItem("resume48LeftSectionOrder");
    localStorage.removeItem("resume48RightSectionOrder");
  };

  const getSectionTitle = (sectionId) => {
    const titles = {
      summary:         'Professional Summary',
      skills:         'Skills',
      experience:     'Work Experience',
      education:      'Education',
      certifications: 'Certifications',
      languages:      'Languages',
      accomplishments:'Accomplishments',
      additionalInfo: 'Additional Information',
      softwareSkills: 'Software Skills',
      volunteering:   'Volunteering',
      interests:      'Interests',
      websites:       'Websites & Profiles',
      references:     'References',
    };
    if (sectionId.startsWith('custom_'))
      return sectionId.replace('custom_', '').replace(/_/g, ' ').toUpperCase();
    return titles[sectionId] || sectionId.toUpperCase();
  };

  const renderDragPanel = (side, sections) => (
    <Box style={{ flex: 1, minWidth: 250 }}>
      <Typography variant="h6" style={{ marginBottom: "12px", fontWeight: 600 }}>
        {side === "left" ? "Left (Sidebar)" : "Right (Main)"} — {sections.length} sections
      </Typography>
      <Box style={{ maxHeight: 400, overflowY: "auto", border: "1px solid #e0e0e0", borderRadius: 4, padding: 8 }}>
        {sections.map((id, idx) => (
          <Paper key={id} draggable
            onDragStart={e => mkDragStart(side, sections)(e, idx)}
            onDragOver={e => handleDragOver(e, idx, side)}
            onDragLeave={handleDragLeave}
            onDrop={e => handleDrop(e, idx, side)}
            onDragEnd={handleDragEnd}
            style={{
              padding: "10px 14px",
              marginBottom: 8,
              display: "flex",
              alignItems: "center",
              backgroundColor: dragOverIndex === idx && dragOverSide === side ? "#f0f0f0" : "white",
              border: dragOverIndex === idx && dragOverSide === side
                ? `2px dashed ${color}`
                : "1px solid #ddd",
              cursor: "grab",
            }}>
            <DragIndicator color="action" style={{ marginRight: 8 }} />
            <Typography style={{ flex: 1, fontSize: "13px" }}>{getSectionTitle(id)}</Typography>
            <Typography variant="body2" color="textSecondary">{idx + 1}</Typography>
          </Paper>
        ))}
        {!sections.length && (
          <Typography style={{ padding: 16, textAlign: "center", color: "#888", fontSize: "13px" }}>
            Drag sections here from the other side.
          </Typography>
        )}
      </Box>
      <Button size="small" variant="outlined" style={{ marginTop: 8 }}
        onClick={() => side === "left"
          ? (setLeftSectionOrder(defaultLeftSectionOrder),  localStorage.removeItem("resume48LeftSectionOrder"))
          : (setRightSectionOrder(defaultRightSectionOrder), localStorage.removeItem("resume48RightSectionOrder"))}>
        Reset {side === "left" ? "Left" : "Right"}
      </Button>
    </Box>
  );

  const getEducationEntries = () => {
    const forResume = localStorage.getItem("educationEntriesForResume");
    if (forResume) return JSON.parse(forResume);

    if (educationEntries.length > 0) {
      return educationEntries.map(entry => {
        if (entry.educationLevel === "school") {
          return {
            id: entry.id,
            degree: `${entry.standard || ""}${entry.standard ? " Standard" : ""}`,
            fieldOfStudy: entry.stream || "General Education",
            schoolName: entry.schoolName || "",
            schoolLocation: entry.schoolLocation || "",
            gradMonth: "", gradYear: "",
            additionalCoursework: [entry.district, entry.percentage && `Percentage: ${entry.percentage}%`].filter(Boolean).join(" | ")
          };
        }
        return {
          id: entry.id,
          degree: `${entry.degreeName || ""}${entry.programType ? ` (${entry.programType})` : ""}`,
          fieldOfStudy: entry.specialization || entry.degreeType || "",
          schoolName: entry.collegeName || "",
          schoolLocation: entry.schoolLocation || "",
          gradMonth: entry.gradMonth || "", gradYear: entry.gradYear || "",
          additionalCoursework: [
            entry.cgpa && `CGPA: ${entry.cgpa}`,
            entry.percentage && `Percentage: ${entry.percentage}%`,
            entry.grade && `Grade: ${entry.grade}`,
            entry.additionalCoursework
          ].filter(Boolean).join(" | ")
        };
      });
    }
    return education;
  };

  const finalEducationEntries = getEducationEntries();
  const fullName = `${formData.firstName || ""} ${formData.lastName || ""}`.trim().toUpperCase() || "RUFUSUS STEWART";
  const position = formData.currentPosition || "Graphic Designer";
  const allSkills = savedSkills.length > 0 ? savedSkills : [];
  const allReferences = references.length > 0 ? references : savedReferences;

  const renderSectionContent = (sectionId, inSidebar = false) => {
    const hStyle = inSidebar ? headingStyle() : rightHeadingStyle();
    const tStyle = inSidebar ? baseText({ color: '#d0e0f5' }) : baseText({ color: '#444' });
    const mainThemeColor = inSidebar ? '#ffffff' : color;
    const subThemeColor = inSidebar ? '#d0e0f5' : '#555';
    const descColor = inSidebar ? '#e8eef7' : '#333';

    switch (sectionId) {
      case 'skills':
        return allSkills.length > 0 ? (
          <div className="r48-section" style={{ ...safeText }}>
            <h3 style={hStyle}>SKILLS</h3>
            <ul style={{ listStyle: 'none', padding: 0, margin: 0, ...tStyle }}>
              {allSkills.map((skill, i) => (
                <li key={i} style={liStyle}>
                  • {typeof skill === 'string'
                    ? skill
                    : skill.name
                      ? `${skill.name}${skill.rating ? ` (${skill.rating}%)` : ''}`
                      : (skill.skillName || skill.skill || '')}
                </li>
              ))}
            </ul>
          </div>
        ) : null;

      case 'education':
        return finalEducationEntries.length > 0 ? (
          <div className="r48-section" style={{ ...safeText }}>
            <h3 style={hStyle}>EDUCATION</h3>
            {finalEducationEntries.map((edu, i) => {
              const startY = edu.startMonth && edu.startYear ? formatDate(edu.startMonth, edu.startYear) : (edu.startYear || "");
              const endY = edu.currentlyStudying ? "Present"
                : (edu.gradMonth && edu.gradYear ? formatDate(edu.gradMonth, edu.gradYear)
                  : (edu.endMonth && edu.endYear ? formatDate(edu.endMonth, edu.endYear)
                    : (edu.gradYear || edu.endYear || "Present")));
              return (
                <div key={edu.id || i} style={{ ...tStyle, marginBottom: `${paragraphSpacing * 1.2}px` }}>
                  <strong style={{ color: mainThemeColor }}>{edu.degree || edu.qualification}</strong>
                  {edu.fieldOfStudy && ` in ${edu.fieldOfStudy}`}<br />
                  <span style={{ color: subThemeColor }}>
                    {edu.schoolName || edu.school || edu.institution || edu.universityName}
                    {edu.schoolLocation && ` – ${edu.schoolLocation}`}
                  </span><br />
                  {startY} – {endY}
                  {edu.additionalCoursework && (
                    <div style={{ marginTop: `${paragraphSpacing / 20}px`, paddingLeft: `${paragraphIndent}px` }}>
                      {edu.additionalCoursework}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ) : null;

      case 'certifications':
        return savedCertifications.length > 0 ? (
          <div className="r48-section" style={{ ...safeText }}>
            <h3 style={hStyle}>CERTIFICATIONS</h3>
            <ul style={{ listStyle: 'none', padding: 0, margin: 0, ...tStyle }}>
              {savedCertifications.map((cert, i) => (
                <li key={i} style={liStyle}>
                  • {cert.name} – {cert.provider}{cert.year && ` (${cert.year})`}
                </li>
              ))}
            </ul>
          </div>
        ) : null;

      case 'languages':
        return savedLanguages.length > 0 ? (
          <div className="r48-section" style={{ ...safeText }}>
            <h3 style={hStyle}>LANGUAGES</h3>
            <ul style={{ listStyle: 'none', padding: 0, margin: 0, ...tStyle }}>
              {savedLanguages.map((lang, i) => (
                <li key={i} style={liStyle}>
                  • {typeof lang === 'string' ? lang : `${lang.name}${lang.level ? ` (${lang.level})` : ''}`}
                </li>
              ))}
            </ul>
          </div>
        ) : null;

      case 'softwareSkills':
        return softwareSkills.length > 0 ? (
          <div className="r48-section" style={{ ...safeText }}>
            <h3 style={hStyle}>SOFTWARE SKILLS</h3>
            <ul style={{ listStyle: 'none', padding: 0, margin: 0, ...tStyle }}>
              {softwareSkills.map((skill, i) => (
                <li key={i} style={liStyle}>
                  • {skill.name} — {skill.level}%
                </li>
              ))}
            </ul>
          </div>
        ) : null;

      case 'interests':
        return savedInterests.length > 0 ? (
          <div className="r48-section" style={{ ...safeText }}>
            <h3 style={hStyle}>INTERESTS</h3>
            <ul style={{ listStyle: 'none', padding: 0, margin: 0, ...tStyle }}>
              {savedInterests.map((interest, i) => (
                <li key={i} style={liStyle}>• {interest}</li>
              ))}
            </ul>
          </div>
        ) : null;

      case 'summary':
        return (profileSummary || savedSummary) ? (
          <div className="r48-section" style={{ ...safeText }}>
            <h3 style={hStyle}>PROFESSIONAL SUMMARY</h3>
            <p style={{ ...tStyle, whiteSpace: 'pre-line', paddingLeft: `${paragraphIndent}px`, margin: 0 }}>
              {profileSummary || savedSummary}
            </p>
          </div>
        ) : null;

      case 'experience':
        return workHistory.length > 0 ? (
          <div className="r48-section" style={{ ...safeText }}>
            <h3 style={hStyle}>WORK EXPERIENCE</h3>
            {workHistory.map((job, i) => {
              const startDate = job.startMonth && job.startYear ? formatDate(job.startMonth, job.startYear) : (job.startYear || job.startDate || "");
              const endDate = job.current || job.currentlyWorking ? "Present"
                : (job.endMonth && job.endYear ? formatDate(job.endMonth, job.endYear) : (job.endYear || job.endDate || "Present"));
              return (
                <div key={i} style={{ marginBottom: `${paragraphSpacing * 1.5}px`, ...safeText }}>
                  <h4 style={{
                    fontFamily: font, fontStyle: fontStyle,
                    fontSize: `calc(${fontSize} * 1.15)`,
                    margin: '0 0 6px 0', color: inSidebar ? '#ffffff' : '#1a3c7a', ...safeText,
                  }}>
                    {job.jobTitle || job.position || job.role}
                  </h4>
                  <p style={{ ...tStyle, margin: '0 0 12px 0', fontStyle: 'italic', color: subThemeColor }}>
                    {job.employer || job.company || job.companyName}
                    {job.location && ` – ${job.location}`}<br />
                    {startDate} – {endDate}
                  </p>
                  {job.description && (
                    <div
                      style={{ paddingLeft: `${paragraphIndent}px`, color: descColor, ...safeText }}
                      dangerouslySetInnerHTML={{
                        __html: wrapHtml(
                           job.description
                             .replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>")
                             .replace(/\*(.*?)\*/g, "<em>$1</em>")
                             .replace(/<u>(.*?)<\/u>/g, "<u>$1</u>")
                             .replace(/\n/g, "<br/>")
                        ),
                      }}
                    />
                  )}
                  {job.responsibilities && job.responsibilities.length > 0 && (
                    <ul style={{ paddingLeft: `${20 + paragraphIndent}px`, margin: '8px 0 0 0', ...tStyle }}>
                      {job.responsibilities.map((duty, idx) => (
                        <li key={idx} style={{ marginBottom: `${paragraphSpacing / 20}px`, ...safeText }}>{duty}</li>
                      ))}
                    </ul>
                  )}
                </div>
              );
            })}
          </div>
        ) : null;

      case 'accomplishments':
        return savedAccomplishments.length > 0 ? (
          <div className="r48-section" style={{ ...safeText }}>
            <h3 style={hStyle}>ACCOMPLISHMENTS</h3>
            <ul style={{ paddingLeft: `${20 + paragraphIndent}px`, margin: '0', ...tStyle }}>
              {savedAccomplishments.map((acc, i) => (
                <li key={i} style={{ marginBottom: `${paragraphSpacing / 20}px`, whiteSpace: 'pre-line', ...safeText }}>{acc}</li>
              ))}
            </ul>
          </div>
        ) : null;

      case 'volunteering':
        return savedVolunteering.length > 0 ? (
          <div className="r48-section" style={{ ...safeText }}>
            <h3 style={hStyle}>VOLUNTEERING</h3>
            {savedVolunteering.map((vol, i) => (
              <div key={i} style={{ marginBottom: `${paragraphSpacing * 1.2}px`, ...safeText }}>
                <h4 style={{
                  fontFamily: font, fontStyle: fontStyle,
                  fontSize: `calc(${fontSize} * 1.1)`,
                  margin: '0 0 6px 0', color: inSidebar ? '#ffffff' : '#1a3c7a', ...safeText,
                }}>
                  {vol.subtopic}
                </h4>
                <p style={{ ...tStyle, margin: '0 0 8px 0', fontStyle: 'italic', color: subThemeColor }}>
                  {vol.fromDate} - {vol.toDate}
                </p>
                {vol.content && (
                  <p style={{ ...tStyle, whiteSpace: 'pre-line', paddingLeft: `${paragraphIndent}px` }}>
                    {vol.content}
                  </p>
                )}
              </div>
            ))}
          </div>
        ) : null;

      case 'websites':
        return savedWebsites.length > 0 ? (
          <div className="r48-section" style={{ ...safeText }}>
            <h3 style={hStyle}>WEBSITES / PROFILES</h3>
            <ul style={{ paddingLeft: `${20 + paragraphIndent}px`, margin: '0', ...tStyle }}>
              {savedWebsites.map((site, i) => {
                if (!site) return null;
                const url = typeof site === 'string' ? site : (site.url || site.link || '');
                const name = typeof site === 'object' ? (site.name || site.label || url) : url;
                return (
                  <li key={i} style={liStyle}>
                    <a href={url.startsWith('http') ? url : `https://${url}`} target="_blank" rel="noopener noreferrer" style={{ color: inSidebar ? '#ffffff' : '#1976d2', textDecoration: 'none' }}>
                      {name || url}
                    </a>
                  </li>
                );
              })}
            </ul>
          </div>
        ) : null;

      case 'additionalInfo':
        return savedAdditionalInfo.length > 0 ? (
          <div className="r48-section" style={{ ...safeText }}>
            <h3 style={hStyle}>ADDITIONAL INFORMATION</h3>
            <ul style={{ paddingLeft: `${20 + paragraphIndent}px`, margin: '0', ...tStyle }}>
              {savedAdditionalInfo.map((info, i) => (
                <li key={i} style={{ marginBottom: `${paragraphSpacing / 20}px`, ...safeText }}>{info}</li>
              ))}
            </ul>
          </div>
        ) : null;

      case 'references':
        return (
          <div className="r48-section" style={{ ...safeText }}>
            <h3 style={hStyle}>REFERENCES</h3>
            {allReferences.length > 0 ? (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '40px', ...tStyle }}>
                {allReferences.map((ref, i) => {
                  if (typeof ref === 'string') {
                    return <div key={i} style={{ whiteSpace: 'pre-line', ...safeText }}>{ref}</div>;
                  }
                  return (
                    <div key={i} style={{ ...safeText }}>
                      <strong style={{ color: mainThemeColor }}>{ref.refName || ref.name || ref.referenceName}</strong><br />
                      {ref.company || ref.companyName}{(ref.position || ref.jobTitle) && ` / ${ref.position || ref.jobTitle}`}<br />
                      {ref.phone && `Phone: ${ref.phone}`}<br />
                      {ref.email && `Email: ${ref.email}`}
                    </div>
                  );
                })}
              </div>
            ) : (
              <p style={{ ...tStyle }}>Available upon request.</p>
            )}
          </div>
        );

      default:
        if (sectionId && sectionId.startsWith('custom_')) {
          const customSectionName = sectionId.replace('custom_', '');
          const items = customSections[customSectionName];
          return items && items.length > 0 ? (
            <div className="r48-section" style={{ ...safeText }}>
              <h3 style={hStyle}>{customSectionName.toUpperCase()}</h3>
              <ul style={{ paddingLeft: `${20 + paragraphIndent}px`, margin: '0', ...tStyle }}>
                {items.map((item, i) => (
                  <li key={i} style={{ marginBottom: `${paragraphSpacing / 20}px`, whiteSpace: 'pre-line', ...safeText }}>{item}</li>
                ))}
              </ul>
            </div>
          ) : null;
        }
        return null;
    }
  };

  const liStyle = {
    marginBottom: `${paragraphSpacing / 10}px`,
    paddingLeft: `${paragraphIndent}px`,
    ...safeText,
  };

  // FIXED: Function to paginate sections properly
  const paginateSections = () => {
    if (!rightContentRef.current) return;

    const sections = Array.from(rightContentRef.current.querySelectorAll(".r48-section"));
    const pageHeight = pagePx.height - (topBottomMargin * 2);
    const headerHeight = 120;

    const pageGroups = [];
    let currentGroup = [];
    let accumulatedHeight = 0;
    let isFirstPage = true;

    sections.forEach((section) => {
      const sectionHeight = section.getBoundingClientRect().height || 100; // fallback height
      const availableHeight = pageHeight - (isFirstPage ? (headerHeight + sectionSpacing) : 0);
      const spacingToUse = currentGroup.length > 0 ? sectionSpacing : 0;

      // If adding this section exceeds the page height, start a new page
      if (accumulatedHeight + sectionHeight + spacingToUse > availableHeight && currentGroup.length > 0) {
        pageGroups.push([...currentGroup]);
        currentGroup = [];
        accumulatedHeight = 0;
        isFirstPage = false;
      }

      currentGroup.push(section);
      accumulatedHeight += sectionHeight + (currentGroup.length > 1 ? sectionSpacing : 0);
    });

    // Push the last group
    if (currentGroup.length > 0) {
      pageGroups.push([...currentGroup]);
    }

    // If no sections, at least one page
    if (pageGroups.length === 0) {
      pageGroups.push([]);
    }

    // Map each group to section IDs
    const allSectionIds = rightSectionOrder.filter(id => renderSectionContent(id) !== null);
    const newPageContents = {};

    pageGroups.forEach((group, index) => {
      const sectionIds = group.map(section => {
        // Find the section ID from the content
        const sectionIndex = Array.from(rightContentRef.current.querySelectorAll(".r48-section")).indexOf(section);
        return allSectionIds[sectionIndex] || null;
      }).filter(id => id !== null);

      newPageContents[index] = sectionIds;
    });

    setTotalPages(Math.max(1, pageGroups.length));
    setPageContents(newPageContents);

    // Clamp current page
    if (currentPage >= pageGroups.length) {
      setCurrentPage(Math.max(0, pageGroups.length - 1));
    }
  };

  // Calculate pagination when dependencies change
  useEffect(() => {
    if (loading) return;

    const timeoutId = setTimeout(() => {
      paginateSections();
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [
    rightSectionOrder,
    savedSummary,
    workHistory,
    educationEntries,
    education,
    savedSkills,
    savedLanguages,
    savedAccomplishments,
    savedCertifications,
    savedReferences,
    references,
    softwareSkills,
    savedVolunteering,
    savedInterests,
    savedWebsites,
    savedAdditionalInfo,
    customSections,
    loading,
  ]);

  // Re-paginate when page size or margins change
  useEffect(() => {
    if (loading) return;
    const timeoutId = setTimeout(() => {
      paginateSections();
    }, 300);
    return () => clearTimeout(timeoutId);
  }, [pageSize, topBottomMargin, sideMargins, sectionSpacing, paragraphSpacing, lineSpacing, fontSize, headingSize]);

  if (loading) {
    return (
      <div style={{ width: page.width, minHeight: page.height, display: 'flex', justifyContent: 'center', alignItems: 'center', fontFamily: font, fontSize: fontSize }}>
        Loading Resume...
      </div>
    );
  }

  // Get sections for a specific page
  const getSectionsForPage = (pageIndex) => {
    return pageContents[pageIndex] || [];
  };

  const renderLeftSidebar = (pageIdx) => (
    <div style={{
      width: '38%',
      minWidth: '38%',
      maxWidth: '38%',
      flexShrink: 0,
      background: `linear-gradient(180deg, ${color} 0%, #1a3c7a 100%)`,
      color: '#e8eef7',
      padding: `${topBottomMargin}px ${sideMargins / 2}px`,
      display: 'flex',
      flexDirection: 'column',
      gap: `${sectionSpacing}px`,
      overflow: 'hidden',
      boxSizing: 'border-box',
      wordBreak: 'break-word',
      overflowWrap: 'break-word',
      height: '100%',
    }}>
      {pageIdx === 0 ? (
        <>
          {/* Photo only on Page 1 */}
          {photo && (
            <div style={{ alignSelf: 'center', flexShrink: 0 }}>
              <img
                src={photo}
                alt={fullName}
                style={{
                  width: '150px', height: '150px',
                  borderRadius: '50%', objectFit: 'cover',
                  border: '6px solid rgba(255,255,255,0.25)',
                  boxShadow: '0 8px 20px rgba(0,0,0,0.4)',
                  display: 'block',
                }}
              />
            </div>
          )}

          {/* CONTACT - appears on page 1 */}
          <div style={{ ...safeText }}>
            <h3 style={headingStyle()}>CONTACT</h3>
            <div style={baseText({ color: '#d0e0f5' })}>
              {formData.phone && (
                <div style={liStyle}>
                  <span style={{ display: 'inline-block', width: '24px', flexShrink: 0 }}>📞</span> {formData.phone}
                </div>
              )}
              {formData.email && (
                <div style={liStyle}>
                  <span style={{ display: 'inline-block', width: '24px', flexShrink: 0 }}>✉️</span> {formData.email}
                </div>
              )}
              {(formData.city || formData.country) && (
                <div style={liStyle}>
                  <span style={{ display: 'inline-block', width: '24px', flexShrink: 0 }}>📍</span>{' '}
                  {[formData.city, formData.country, formData.pinCode].filter(Boolean).join(", ")}
                </div>
              )}
              {formData.linkedIn && (
                <div style={liStyle}>
                  <span style={{ display: 'inline-block', width: '24px', flexShrink: 0 }}>🔗</span>{' '}
                  <a
                    href={formData.linkedIn.startsWith('http') ? formData.linkedIn : `https://${formData.linkedIn}`}
                    target="_blank" rel="noopener noreferrer"
                    style={{ color: '#ffffff', textDecoration: 'none', opacity: 0.9, wordBreak: 'break-all' }}
                  >
                    {formData.linkedIn.replace(/^https?:\/\/(www\.)?linkedin\.com\/in\/|linkedin\.com\/in\//, '')}
                  </a>
                </div>
              )}
              {formData.website && (
                <div style={liStyle}>
                  <span style={{ display: 'inline-block', width: '24px', flexShrink: 0 }}>🌐</span>{' '}
                  <a
                    href={formData.website.startsWith('http') ? formData.website : `https://${formData.website}`}
                    target="_blank" rel="noopener noreferrer"
                    style={{ color: '#ffffff', textDecoration: 'none', opacity: 0.9, wordBreak: 'break-all' }}
                  >
                    {formData.website.replace(/^https?:\/\/(www\.)?/, '')}
                  </a>
                </div>
              )}
              {formData.drivingLicence && (
                <div style={liStyle}>
                  <span style={{ display: 'inline-block', width: '24px', flexShrink: 0 }}>🚗</span> DL: {formData.drivingLicence}
                </div>
              )}
            </div>
          </div>

          {/* Left sidebar sections - shown on page 1 */}
          {leftSectionOrder.map(sectionId => {
            return <React.Fragment key={sectionId}>{renderSectionContent(sectionId, true)}</React.Fragment>;
          })}
        </>
      ) : (
        <div style={{ height: '100%' }} />
      )}
    </div>
  );

  const renderRightHeader = () => (
    <div style={{ ...safeText, flexShrink: 0 }}>
      <h1 style={{
        fontFamily: font, fontStyle: fontStyle,
        fontSize: `calc(${headingSize} * 1.5)`,
        fontWeight: 'bold', margin: '0 0 8px 0',
        color: color, lineHeight: '1.05',
        ...safeText,
      }}>
        {fullName}
      </h1>
      <h2 style={{
        fontFamily: font, fontStyle: fontStyle,
        fontSize: `calc(${fontSize} * 1.3)`,
        fontWeight: '600', margin: '0 0 20px 0',
        color: '#1a3c7a', ...safeText,
      }}>
        {position}
      </h2>
      <hr style={{ border: 'none', borderTop: `${lineWeight * 3}px solid #1a3c7a`, margin: '12px 0 28px 0', width: '120px' }} />
    </div>
  );

  // Render a single page
  const renderPage = (pageIdx) => {
    const pageSectionIds = getSectionsForPage(pageIdx);

    return (
      <div
        key={pageIdx}
        className="resume-page"
        style={{
          width: page.width,
          height: page.height,
          backgroundColor: '#ffffff',
          color: '#333333',
          fontFamily: font,
          fontSize: fontSize,
          fontStyle: fontStyle,
          lineHeight: `${lineSpacing / 20}`,
          display: 'flex',
          margin: '0 auto',
          boxShadow: exporting ? 'none' : '0 4px 25px rgba(0,0,0,0.12)',
          overflow: 'hidden',
          boxSizing: 'border-box',
          ...(exporting && {
            pageBreakAfter: 'always',
            marginBottom: '0',
          }),
        }}
      >
        {renderLeftSidebar(pageIdx)}

        <div style={{
          flex: 1,
          minWidth: 0,
          maxWidth: '62%',
          padding: `${topBottomMargin}px ${sideMargins}px`,
          backgroundColor: '#ffffff',
          display: 'flex',
          flexDirection: 'column',
          gap: `${sectionSpacing}px`,
          overflow: 'hidden',
          boxSizing: 'border-box',
          wordBreak: 'break-word',
          overflowWrap: 'break-word',
          height: '100%',
        }}>
          {pageIdx === 0 && renderRightHeader()}

          {pageSectionIds.map(sectionId => {
            return <React.Fragment key={sectionId}>{renderSectionContent(sectionId, false)}</React.Fragment>;
          })}
        </div>
      </div>
    );
  };

  return (
    <div style={{ width: '100%', position: 'relative' }}>
      {!exporting && (
        <Box style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 16, flexWrap: "wrap", justifyContent: "center" }}>
          <IconButton
            onClick={() => setCurrentPage(p => Math.max(0, p - 1))}
            disabled={currentPage === 0}
            size="small"
            color="primary"
          >
            <ChevronLeft />
          </IconButton>
          <Typography variant="body2">Page {currentPage + 1} / {totalPages}</Typography>
          <IconButton
            onClick={() => setCurrentPage(p => Math.min(totalPages - 1, p + 1))}
            disabled={currentPage >= totalPages - 1}
            size="small"
            color="primary"
          >
            <ChevronRight />
          </IconButton>
          {enableDragDrop && (
            <>
              <Button onClick={() => setArrangeDialogOpen(true)} variant="contained" startIcon={<Reorder />}>
                Arrange Sections
              </Button>
              <Button onClick={resetAll} variant="outlined" color="secondary">
                Reset All
              </Button>
            </>
          )}
        </Box>
      )}

      {/* Hidden measurement container - used for pagination calculation */}
      <Box
        ref={rightContentRef}
        sx={{
          position: "absolute",
          visibility: "hidden",
          pointerEvents: "none",
          width: `${pagePx.width * 0.62 - sideMargins * 2}px`,
          top: 0,
          left: -9999,
          zIndex: -1,
        }}
      >
        {rightSectionOrder.map((sectionId) => {
          const content = renderSectionContent(sectionId);
          return content ? <React.Fragment key={sectionId}>{content}</React.Fragment> : null;
        })}
      </Box>

      {/* Pages Wrapper */}
      <div id="resume-container">
        {exporting ? (
          // Render all pages for export
          Array.from({ length: totalPages }).map((_, pageIdx) => renderPage(pageIdx))
        ) : (
          // Render only current page for viewing
          renderPage(currentPage)
        )}
      </div>

      {/* Arrange Sections Dialog */}
      <Dialog open={arrangeDialogOpen} onClose={() => setArrangeDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Reorder /> Arrange Resume Sections
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" style={{ marginBottom: 16 }}>
            Drag to reorder sections or move them between sidebar and main content area.
          </Typography>
          <Box style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
            {renderDragPanel("left",  leftSectionOrder)}
            {renderDragPanel("right", rightSectionOrder)}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={resetAll} color="secondary">Reset All</Button>
          <Button variant="contained" onClick={() => setArrangeDialogOpen(false)}>Done</Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default Resume48;